import { useState, useEffect, useRef, useMemo } from "react";
import { Dialog } from "primereact/dialog";
import { Column } from "primereact/column";
import { TreeTable } from "primereact/treetable";
import { Button } from "primereact/button";
import { ConfirmDialog } from "../shared/ConfirmDialog";
import AppToast from "../../../../shared/components/AppToast";
import { ensureCatalogAuth } from "../../../../services/backend/authService";
import { getFamiliasAll } from "../../../../services/backend/catalogService";
import logger from "../../../../shared/utils/logger";

const buildChildren = (hijos, parentKey) => {
  if (!Array.isArray(hijos)) return [];
  return hijos.map((hijo, index) => ({
    key: `${parentKey}-${index}`,
    data: {
      name: hijo.nombre || "",
      id: hijo.id || 0,
      activa: hijo.activa ?? true,
    },
    children: buildChildren(hijo.hijos, `${parentKey}-${index}`),
  }));
};

const buildTreeNodes = (familias) => {
  return familias.map((familia, index) => ({
    key: String(index),
    data: {
      name: familia.nombre || "",
      id: familia.id || 0,
      activa: familia.activa ?? true,
    },
    children: buildChildren(familia.hijos, String(index)),
  }));
};

const flattenNodes = (nodes) => {
  let result = [];
  for (const node of nodes) {
    result.push(node);
    if (node.children?.length) {
      result = result.concat(flattenNodes(node.children));
    }
  }
  return result;
};

const findSelectedKeys = (nodes, idsSet) => {
  const keys = {};
  for (const node of nodes) {
    if (idsSet.has(String(node.data.id))) {
      keys[node.key] = { checked: true, partialChecked: false };
    } else if (node.children?.length) {
      const childKeys = findSelectedKeys(node.children, idsSet);
      const hasChecked = Object.keys(childKeys).some(
        (k) => childKeys[k]?.checked
      );
      if (hasChecked) {
        keys[node.key] = { checked: false, partialChecked: true };
        Object.assign(keys, childKeys);
      }
    }
  }
  return keys;
};

const getSelectedIds = (nodes, keys) => {
  const ids = [];
  for (const node of nodes) {
    const keyData = keys[node.key];
    if (keyData?.checked) {
      ids.push(node.data.id);
    } else if (node.children?.length) {
      ids.push(...getSelectedIds(node.children, keys));
    }
  }
  return ids;
};

const countSelected = (nodes, keys) => {
  let count = 0;
  for (const node of nodes) {
    const keyData = keys[node.key];
    if (keyData?.checked) {
      count++;
    } else if (node.children?.length) {
      count += countSelected(node.children, keys);
    }
  }
  return count;
};

export const SeleccionarFamiliasDialog = ({
  visible,
  onHide,
  onConfirm,
  idsIniciales = "",
  tipo,
}) => {
  const toastRef = useRef(null);
  const [allFamilias, setAllFamilias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState({});
  const [expandedKeys, setExpandedKeys] = useState({});
  const [confirmVisible, setConfirmVisible] = useState(false);

  const nodes = useMemo(() => buildTreeNodes(allFamilias), [allFamilias]);
  const allFlatNodes = useMemo(() => flattenNodes(nodes), [nodes]);

  useEffect(() => {
    if (visible) {
      let mounted = true;
      const load = async () => {
        setLoading(true);
        try {
          await ensureCatalogAuth();
          const response = await getFamiliasAll({ tipo, activas: true });
          const items = Array.isArray(response?.familias)
            ? response.familias
            : Array.isArray(response)
            ? response
            : [];
          if (mounted) setAllFamilias(items);
        } catch (err) {
          logger.error("Error al cargar familias:", err);
          if (mounted) setAllFamilias([]);
        } finally {
          if (mounted) setLoading(false);
        }
      };
      load();
      setSelectedKeys({});
      setExpandedKeys({});
      return () => {
        mounted = false;
      };
    }
  }, [tipo, visible]);

  useEffect(() => {
    if (visible && allFlatNodes.length && idsIniciales) {
      const idsSet = new Set(
        idsIniciales
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      );
      setSelectedKeys(findSelectedKeys(nodes, idsSet));
    }
  }, [visible, allFlatNodes, idsIniciales, nodes]);

  const selectedCount = useMemo(
    () => countSelected(nodes, selectedKeys),
    [nodes, selectedKeys]
  );

  const handleConfirm = () => {
    const ids = getSelectedIds(nodes, selectedKeys);
    onConfirm?.(ids.join(","));
    onHide();
  };

  const initialIdsSet = useMemo(
    () =>
      new Set(
        idsIniciales
          .split(",")
          .map((c) => c.trim())
          .filter(Boolean)
      ),
    [idsIniciales]
  );

  const currentIdsSet = useMemo(
    () => new Set(getSelectedIds(nodes, selectedKeys).map(String)),
    [nodes, selectedKeys]
  );

  const hasChanges =
    currentIdsSet.size !== initialIdsSet.size ||
    [...currentIdsSet].some((id) => !initialIdsSet.has(id));

  const handleHide = () => {
    if (hasChanges) {
      setConfirmVisible(true);
    } else {
      onHide();
    }
  };

  const handleDiscard = () => {
    setConfirmVisible(false);
    onHide();
  };

  const header = (
    <div className="flex align-items-center justify-content-between flex-wrap gap-2">
      <span className="text-sm font-semibold">
        Seleccionar familias ({selectedCount} seleccionadas)
      </span>
      <Button
        className="p-button-rounded p-button-sm"
        icon="pi pi-check"
        label="Confirmar"
        onClick={handleConfirm}
        disabled={!selectedCount}
        severity="success"
      />
    </div>
  );

  const footer = (
    <div className="flex justify-content-end gap-2">
      <Button className="p-button-text" label="Cancelar" onClick={handleHide} />
    </div>
  );

  return (
    <>
      <AppToast ref={toastRef} />
      <ConfirmDialog
        visible={confirmVisible}
        onCancel={() => setConfirmVisible(false)}
        onConfirm={handleDiscard}
        header="Descartar cambios"
        message="Tiene familias seleccionadas sin confirmar. ¿Desea descartar la selección?"
      />
      <Dialog
        header="Seleccionar Familias"
        visible={visible}
        onHide={handleHide}
        footer={footer}
        style={{ width: "80vw" }}
        maximizable
      >
        <TreeTable
          value={nodes}
          header={header}
          paginator
          rows={10}
          rowsPerPageOptions={[5, 10, 25, 50]}
          scrollable
          scrollHeight="50vh"
          loading={loading}
          selectionMode="checkbox"
          selectionKeys={selectedKeys}
          onSelectionChange={(e) => setSelectedKeys(e.value)}
          expandedKeys={expandedKeys}
          onToggle={(e) => setExpandedKeys(e.value)}
          tableStyle={{ minWidth: "30rem" }}
        >
          <Column field="name" header="Nombre" expander />
          <Column field="id" header="ID" style={{ width: "20%" }} />
        </TreeTable>
      </Dialog>
    </>
  );
};
